import type { InjectionKey, Ref } from 'vue';
import type { IngredientEditorMethods } from '~/types';

export const ingredientEditorKey: InjectionKey<Ref<IngredientEditorMethods | undefined>> = Symbol('ingredientEditor');

export const qAndA: { question: string; answer: string }[] = [
    {
        question: 'What sizes are available?',
        answer: 'Sizes are visible on each product page. However, if you require a different size, please get'
            + ' in touch.'
    },
    {
        question: 'What flavours are available?',
        answer: 'The most popular cakes are chocolate and vanilla and their fruit variations, Biscoff, Dobos,'
            + ' lemon, and black forest cake. However, you may order any flavours.'
    },
    {
        question: 'Do you offer delivery?',
        answer: 'I do! Starting from £5.00, depending on the distance. Please contact me to discuss.'
    },
    {
        question: 'Do you offer free from cakes?',
        answer: 'Unfortunately, I do not offer free from cakes at the moment.'
    },
    {
        question: 'How much notice do you need for an order?',
        answer: 'I require at least 2 weeks notice for all orders. However, I may be able to accommodate '
            + 'shorter notice orders. Please contact me to discuss.'
    },
    {
        question: 'Do you make shaped cakes?',
        answer: 'Unfortunately, I do not make shaped cakes at the moment.'
    },
    {
        question: 'Do you cater for events?',
        answer: 'Yes, I do! Be it a birthday, wedding or any other event. Please contact me to discuss your '
            + 'requirements.'
    },
    {
        question: 'Is there a maximum order size?',
        answer: 'Depends on capacity. Please get in touch to discuss your requirements.'
    },
    {
        question: 'Do you require a deposit?',
        answer: 'Yes, I require a deposit to secure your order. The amount is dependent on the order size. '
            + 'The remaining balance is due on collection or before delivery.'
    },
    {
        question: 'Do you offer cake tastings?',
        answer: 'Unfortunately, I do not offer cake tastings at the moment.'
    },
    {
        question: 'Do you accept returns?',
        answer: 'Unfortunately, I do not accept returns. After initial check at the point of collection, '
            + 'the sale is final.'
    },
    {
        question: 'What payment methods do you accept?',
        answer: 'I accept cash, card and bank transfers.'
    }
];

export const title = 'Cina\'s Delicious Cakes – Handcrafted Cakes in Norwich & Norfolk';
export const description = 'Cina\'s Delicious Cakes is a home-based cake business in Norwich. '
    + 'I make cakes for all occasions, including birthdays, weddings, and other events.';

export const locations = [
    'Horsford',
    'Hellesdon',
    'Norwich',
    'Spixworth',
    'Norfolk',
    'Old Catton',
    'Horsham St Faith',
    'Taverham',
    'Drayton'
];

// Unique copy per location page, so Google doesn't treat them as near-duplicates of one another.
// Distances are as the crow flies from the address below.
export const locationPages: Record<string, {
    heading: string;
    metaDescription: string;
    intro: string;
    local: string;
    delivery: string;
    products: { slug: string; name: string }[];
    nearby: string[];
}> = {
    Norwich: {
        heading: 'Birthday & Celebration Cakes in Norwich',
        metaDescription: 'Handmade birthday, wedding and celebration cakes from a home bakery in north Norwich (NR6). '
            + 'Custom designs, Hungarian classics, collection or delivery across the city.',
        intro: 'I bake every cake to order from my home kitchen in NR6, on the north side of Norwich. No shop '
            + 'counter and no cakes sitting in a fridge for days: your cake is made for your date, to your design.',
        local: 'Most of my orders go to homes, halls and restaurants around the city, from Sprowston and Thorpe '
            + 'St Andrew to Eaton and Costessey. Birthday cakes are the bulk of it, but I also make anniversary '
            + 'cakes, christening cakes and trays of slices for office leaving dos. If you have a design in mind, '
            + 'send me a photo and I will tell you honestly what I can do with it.',
        delivery: 'The city centre is roughly three miles from my kitchen. You can collect at the weekend, or I '
            + 'can deliver anywhere in Norwich from £5 depending on distance. Please give me at least two weeks\' '
            + 'notice; for anything shorter, get in touch and I will see what I can fit in.',
        products: [
            { slug: 'chocolate-cake', name: 'Chocolate cake' },
            { slug: 'dobos-cake', name: 'Dobos torte' },
            { slug: 'raspberry-cheesecake', name: 'Raspberry cheesecake' }
        ],
        nearby: ['Old Catton', 'Hellesdon', 'Norfolk']
    },
    Norfolk: {
        heading: 'Handmade Cakes Across Norfolk',
        metaDescription: 'Bespoke birthday and celebration cakes made to order near Norwich. Delivering across '
            + 'Broadland and the wider Norfolk area, with collection from north Norwich.',
        intro: 'Cina\'s Delicious Cakes is a small home bakery run by one baker, just north of Norwich, and plenty of my '
            + 'customers come from further out in the county: Wroxham, Aylsham, Wymondham and the villages in '
            + 'between.',
        local: 'For a village hall party, a wedding at a barn venue or a big family birthday, I can make tiered '
            + 'cakes, sheet cakes for a crowd or a mixed box of Hungarian slices so guests get a bit of '
            + 'everything. My Esterházy and Somlói galuska are rarely found anywhere else in Norfolk, so they are '
            + 'a good choice if you want something people will talk about.',
        delivery: 'Delivery outside Norwich is priced by distance, starting from £5. Many customers from further '
            + 'afield find it easier to collect on a Saturday or Sunday when they are in town. Message me with '
            + 'your postcode and date and I will give you an exact price.',
        products: [
            { slug: 'esterhazy-cake', name: 'Esterházy cake' },
            { slug: 'somloi-galuska', name: 'Somlói galuska' },
            { slug: 'traditional-english-fruit-cake', name: 'Traditional English fruit cake' }
        ],
        nearby: ['Norwich', 'Taverham', 'Horsford']
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'Old Catton': {
        heading: 'Cakes in Old Catton',
        metaDescription: 'Home-baked cakes made less than a mile from Old Catton. Birthday cakes, Hungarian '
            + 'pastries and celebration bakes to order, with easy collection or local delivery.',
        intro: 'Old Catton is practically on my doorstep: the kitchen is under a mile away, so most Catton '
            + 'customers simply pop over to collect.',
        local: 'With Catton Park nearby, summer picnics and garden parties are a regular reason for orders here. '
            + 'Slices travel far better than a tall cream cake on a warm day, so for outdoor events I usually '
            + 'suggest a tray of Isler biscuits, cardinal slices or brownies instead of one large cake.',
        delivery: 'Collection is easiest, but if you would rather not leave the house on the day, Old Catton '
            + 'is about as close as my deliveries get. Please order two weeks ahead where you can.',
        products: [
            { slug: 'isler', name: 'Isler' },
            { slug: 'cardinal-slice', name: 'Cardinal slice' },
            { slug: 'brownie', name: 'Brownie' }
        ],
        nearby: ['Spixworth', 'Horsham St Faith', 'Norwich']
    },
    Spixworth: {
        heading: 'Cakes in Spixworth',
        metaDescription: 'Custom birthday cakes and Hungarian bakes made under two miles from Spixworth. '
            + 'Order a cake for collection or local delivery.',
        intro: 'Spixworth is under two miles from my kitchen, which makes it one of the easiest places for me '
            + 'to deliver to.',
        local: 'Children\'s birthdays keep me busiest in Spixworth. A chocolate or Kinder Bueno cake is the '
            + 'usual favourite with younger guests, and a Black Forest cake goes down well with the grown-ups. '
            + 'Tell me the age, the theme and how many people it needs to feed, and I will suggest a size.',
        delivery: 'Collect at the weekend or have it delivered: Spixworth sits within my nearest delivery zone. '
            + 'A deposit secures your date, and the rest is due on collection or before delivery.',
        products: [
            { slug: 'kinder-bueno-cake', name: 'Kinder Bueno cake' },
            { slug: 'black-forest-cake', name: 'Black Forest cake' },
            { slug: 'oreo-cake', name: 'Oreo cake' }
        ],
        nearby: ['Old Catton', 'Horsham St Faith', 'Horsford']
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'Horsham St Faith': {
        heading: 'Cakes in Horsham St Faith',
        metaDescription: 'Fresh, made-to-order cakes for Horsham St Faith, about a mile and a half from my '
            + 'Norwich kitchen. Birthday cakes, slices and Hungarian specialities.',
        intro: 'Horsham St Faith is about a mile and a half from my kitchen, just on the other side of the '
            + 'airport.',
        local: 'It is a village with a long history (the old priory, and the airfield that became Norwich '
            + 'Airport), and I like baking cakes with a bit of history too. Gerbeaud slice and Dobos torte are '
            + 'both classic recipes from 19th-century Budapest, and they make a lovely change from a sponge for '
            + 'an anniversary or family get-together.',
        delivery: 'I deliver to Horsham St Faith and Newton St Faith, or you can collect at the weekend. Two '
            + 'weeks\' notice is ideal, especially for tiered or decorated cakes.',
        products: [
            { slug: 'gerbeaud', name: 'Gerbeaud slice' },
            { slug: 'dobos-cake', name: 'Dobos torte' },
            { slug: 'chestnut-cream-slice', name: 'Chestnut cream slice' }
        ],
        nearby: ['Horsford', 'Spixworth', 'Hellesdon']
    },
    Hellesdon: {
        heading: 'Cakes in Hellesdon',
        metaDescription: 'Birthday and celebration cakes made to order two miles from Hellesdon. Hungarian '
            + 'pastries, cheesecakes and custom designs, collected or delivered.',
        intro: 'Hellesdon is about two miles from my kitchen and is one of the areas I deliver to most.',
        local: 'Hellesdon orders tend to be for family occasions: big birthdays, christenings and Sunday '
            + 'get-togethers. Cheesecakes are a popular choice here, and the Biscoff and pistachio versions '
            + 'disappear fastest. If you can\'t decide, I can split a large order between two or three flavours.',
        delivery: 'Delivery to Hellesdon starts from £5, or you can collect on a Saturday or Sunday. Order at '
            + 'least two weeks ahead to be sure of your date.',
        products: [
            { slug: 'biscoff-cheesecake', name: 'Biscoff cheesecake' },
            { slug: 'pistachio-cheesecake', name: 'Pistachio cheesecake' },
            { slug: 'carrot-cake', name: 'Carrot cake' }
        ],
        nearby: ['Drayton', 'Norwich', 'Horsham St Faith']
    },
    Horsford: {
        heading: 'Cakes in Horsford',
        metaDescription: 'Home-baked cakes for Horsford, under three miles from my north Norwich kitchen. '
            + 'Birthday cakes, fruit cakes and Hungarian bakes made to order.',
        intro: 'Horsford is just under three miles up the road from my kitchen, so it is an easy delivery.',
        local: 'A lot of Horsford orders are for walks and gatherings out by Horsford Woods, where something '
            + 'sturdy and easy to slice works best. A traditional fruit cake, a carrot cake or a tray of '
            + 'Hungarian potato scones will survive a car boot and a picnic blanket much better than a '
            + 'cream-filled sponge.',
        delivery: 'I deliver to Horsford and the surrounding lanes from £5, or you can collect at the weekend. '
            + 'Get in touch at least two weeks before your date.',
        products: [
            { slug: 'traditional-english-fruit-cake', name: 'Traditional English fruit cake' },
            { slug: 'hungarian-potato-scone', name: 'Hungarian potato scone' },
            { slug: 'lemon-poppy-seeds-cake', name: 'Lemon poppy seed cake' }
        ],
        nearby: ['Horsham St Faith', 'Taverham', 'Drayton']
    },
    Drayton: {
        heading: 'Cakes in Drayton',
        metaDescription: 'Custom cakes and continental bakes for Drayton, under three miles from my Norwich '
            + 'kitchen. Birthday cakes and Hungarian pastries, collected or delivered.',
        intro: 'Drayton is a short drive from my kitchen, just under three miles.',
        local: 'Plenty of my Drayton customers grew up with continental bakes and come to me for the cakes '
            + 'they can\'t find in a supermarket: Marlenka honey cake, Rigó Jancsi and proper Hungarian '
            + 'kaiser rolls. Others try them for the first time at a party and come back for more.',
        delivery: 'I deliver to Drayton from £5 depending on distance, or you can collect at the weekend. For '
            + 'larger orders, please get in touch at least two weeks in advance.',
        products: [
            { slug: 'marlenka', name: 'Marlenka honey cake' },
            { slug: 'rigo-jancsi-chocolate-slice', name: 'Rigó Jancsi chocolate slice' },
            { slug: 'kaiser-roll', name: 'Kaiser roll' }
        ],
        nearby: ['Taverham', 'Hellesdon', 'Horsford']
    },
    Taverham: {
        heading: 'Cakes in Taverham',
        metaDescription: 'Made-to-order birthday cakes, cupcakes and Hungarian bakes for Taverham, about four '
            + 'miles from my Norwich kitchen. Collection or delivery.',
        intro: 'Taverham is about four miles from my kitchen, out along the Wensum valley, and well within my '
            + 'delivery area.',
        local: 'Cupcakes are the usual Taverham order: easy to share at a school party or a children\'s '
            + 'birthday, with no knife needed. I make chocolate, lemon, carrot and Baileys versions, and can mix '
            + 'flavours in one box. For a centrepiece, a Sacher cake is rich and dense enough to feed a crowd '
            + 'with thin slices.',
        delivery: 'Delivery to Taverham is priced by distance from £5, or you can collect at the weekend. Please '
            + 'give me at least two weeks\' notice.',
        products: [
            { slug: 'chocolate-cupcake', name: 'Chocolate cupcake' },
            { slug: 'lemon-cupcake', name: 'Lemon cupcake' },
            { slug: 'sacher-cake', name: 'Sacher cake' }
        ],
        nearby: ['Drayton', 'Horsford', 'Norfolk']
    }
};

export const address = {
    street: '52 Evans Way',
    city: 'Norwich',
    county: 'Norfolk',
    postcode: 'NR6 7LR',
    latitude: 52.66922089212267,
    longitude: 1.2930680535440677
};

export interface LandingPage {
    heading: string;
    metaDescription: string;
    intro: string;
    image: { src: string; width: number; height: number; alt: string };
    secondaryLink?: { to: string; label: string };
    sections: { heading: string; paragraphs: string[]; table?: { headers: string[]; rows: string[][] } }[];
    productsHeading?: string;
    products?: { slug: string; name: string; blurb?: string }[];
    faqs?: { question: string; answer: string }[];
}

// Prices quoted here mirror the product catalogue; update them together.
export const landingPages = {
    birthdayCakes: {
        heading: 'Birthday Cakes in Norwich',
        metaDescription: 'Handmade birthday cakes to order in Norwich. Chocolate, Kinder Bueno, Black Forest and '
            + 'Hungarian cakes from 16 cm to 22 cm, with collection or delivery across Norwich and Norfolk.',
        intro: 'Every birthday cake I make is baked to order in my Norwich kitchen, decorated for the person '
            + 'it is for, and ready on the day you need it.',
        image: { src: '/images/birthday-cake.jpg', width: 1500, height: 2000, alt: 'A decorated birthday cake' },
        secondaryLink: { to: '/products?type=cake', label: 'See All Cakes' },
        sections: [
            {
                heading: 'How ordering works',
                paragraphs: [
                    'Send me a message with the date, how many people the cake needs to feed and any theme or '
                    + 'colours you have in mind. A photo of a cake you like helps a lot. I will reply with a '
                    + 'suggestion and a price, and a deposit secures your date.',
                    'I ask for at least two weeks\' notice. Last-minute birthdays happen, so if your date is '
                    + 'sooner, get in touch anyway and I will tell you honestly whether I can fit it in.'
                ]
            },
            {
                heading: 'Sizes and prices',
                paragraphs: [
                    'Round cakes come in 16, 18, 20 and 22 cm. Most celebration cakes start at around £38 to £45 '
                    + 'for a 16 cm cake, depending on the recipe, with decoration priced on top. Each cake\'s page '
                    + 'shows the exact price for every size.'
                ],
                table: {
                    headers: ['Size', 'Roughly serves'],
                    rows: [['16 cm', '8–10'], ['18 cm', '10–12'], ['20 cm', '14–16'], ['22 cm', '18–20']]
                }
            },
            {
                heading: 'Children\'s and grown-up birthdays',
                paragraphs: [
                    'For children\'s parties, the chocolate, Oreo and Kinder Bueno cakes are the safe bets. For '
                    + 'milestone birthdays, many customers choose one of my Hungarian cakes, such as Dobos torte or '
                    + 'Esterházy, for something guests won\'t have tried before. I don\'t make 3D shaped cakes, '
                    + 'but I can decorate to almost any theme.'
                ]
            }
        ],
        productsHeading: 'Popular birthday cakes',
        products: [
            { slug: 'chocolate-cake', name: 'Chocolate cake', blurb: 'The classic: rich chocolate sponge and cream.' },
            { slug: 'kinder-bueno-cake', name: 'Kinder Bueno cake', blurb: 'The favourite for children\'s parties.' },
            { slug: 'black-forest-cake', name: 'Black Forest cake', blurb: 'Chocolate sponge, cherries and cream.' },
            { slug: 'oreo-cake', name: 'Oreo cake', blurb: 'Cookies-and-cream filling, popular with teenagers.' },
            { slug: 'esterhazy-cake', name: 'Esterházy cake', blurb: 'Walnut layers for a grown-up celebration.' },
            { slug: 'caramel-cake', name: 'Caramel cake' }
        ],
        faqs: [
            {
                question: 'Can you write a name or message on the cake?',
                answer: 'Yes. Tell me the wording when you order and I will include it in the design.'
            },
            {
                question: 'Do you deliver birthday cakes?',
                answer: 'Yes, delivery across Norwich and nearby villages starts from £5 depending on distance. '
                    + 'You can also collect at the weekend.'
            },
            {
                question: 'Do you make gluten-free or vegan birthday cakes?',
                answer: 'Unfortunately not at the moment. Allergens for every cake are listed on its page.'
            }
        ]
    },
    weddingCakes: {
        heading: 'Wedding Cakes in Norwich & Norfolk',
        metaDescription: 'Bespoke wedding cakes made by hand near Norwich. Tiered cakes, European-style flavours and '
            + 'dessert trays for weddings across Norfolk, with delivery to your venue.',
        intro: 'A wedding cake should taste as good as it looks. I design each one around your day, your '
            + 'colours and your favourite flavours, and bake it fresh for your date.',
        image: { src: '/images/wedding-cake.jpg', width: 1500, height: 2000, alt: 'A tiered wedding cake' },
        secondaryLink: { to: '/galleries/wedding-and-valentine-cakes', label: 'Wedding Gallery' },
        sections: [
            {
                heading: 'From first message to the big day',
                paragraphs: [
                    'Start by sending me your date, venue, guest numbers and any inspiration photos. We will talk '
                    + 'through tiers, flavours and decoration, then I will send you a design and a quote. A '
                    + 'deposit books your date, and the balance is due before delivery.',
                    'Summer weekends book up early, so get in touch as soon as you have a date, even if the '
                    + 'details are still taking shape.'
                ]
            },
            {
                heading: 'Flavours',
                paragraphs: [
                    'Each tier can be a different flavour. Popular choices are chocolate, vanilla with fresh fruit, '
                    + 'lemon, Biscoff and Black Forest. If you want something a little different, Dobos and '
                    + 'Esterházy are Hungarian classics that make a striking tier.'
                ]
            },
            {
                heading: 'Dessert tables',
                paragraphs: [
                    'Alongside the cake, or instead of one, I can fill a dessert table with trays of Hungarian '
                    + 'slices, cupcakes and mini desserts, so guests can pick a little of everything.'
                ]
            },
            {
                heading: 'Delivery to your venue',
                paragraphs: [
                    'I deliver wedding cakes to venues across Norwich and Norfolk. Delivery is priced by distance; '
                    + 'tell me the venue and I will include it in your quote.'
                ]
            }
        ],
        productsHeading: 'Ideas for your dessert table',
        products: [
            { slug: 'mini-honey-cake', name: 'Mini honey cakes' },
            { slug: 'french-cream-pastry', name: 'French cream pastry' },
            { slug: 'snow-ball', name: 'Snow balls' },
            { slug: 'chocolate-orange-dome', name: 'Chocolate orange dome' }
        ],
        faqs: [
            {
                question: 'Do you offer cake tastings?',
                answer: 'Not at the moment, but I am happy to describe each flavour in detail, and many '
                    + 'couples order a small cake or a box of slices beforehand to try.'
            },
            {
                question: 'How far ahead should we book?',
                answer: 'As early as you can. I need at least two weeks for any order, but weekends in the '
                    + 'wedding season fill up months ahead.'
            }
        ]
    },
    cupcakes: {
        heading: 'Cupcakes in Norwich',
        metaDescription: 'Handmade cupcakes in Norwich: chocolate, lemon, Biscoff, Baileys, carrot and tiramisu. '
            + 'Boxes of 4, 6 or 12 from £12, for parties, gifts and offices.',
        intro: 'Cupcakes are the easiest way to feed a party: no knife, no plates, and everyone gets their own. '
            + 'I bake them to order in boxes of 4, 6 or 12.',
        image: {
            src: '/images/categories/cupcake.jpg', width: 4032, height: 3024, alt: 'A box of decorated cupcakes'
        },
        secondaryLink: { to: '/products?type=cupcake', label: 'All Cupcakes' },
        sections: [
            {
                heading: 'Prices',
                paragraphs: [
                    'A box of 4 lemon cupcakes starts at £12, and boxes of 6 start at £15.50. A dozen costs £27 to '
                    + '£41 depending on the flavour. Each flavour\'s page has its full price list.'
                ]
            },
            {
                heading: 'Mixing flavours',
                paragraphs: [
                    'For larger orders I can split a box between flavours, which is a good way to please '
                    + 'everyone at a school party or an office birthday. Let me know the mix when you order.'
                ]
            }
        ],
        productsHeading: 'Flavours',
        products: [
            { slug: 'chocolate-cupcake', name: 'Chocolate cupcake' },
            { slug: 'lemon-cupcake', name: 'Lemon cupcake' },
            { slug: 'biscoff-cupcake', name: 'Biscoff cupcake' },
            { slug: 'baileys-cupcake', name: 'Baileys cupcake' },
            { slug: 'carrot-cupcake', name: 'Carrot cupcake' },
            { slug: 'tiramisu-cupcake', name: 'Tiramisu cupcake' },
            { slug: 'pumpkin-brownie-cupcake', name: 'Pumpkin brownie cupcake' }
        ]
    },
    hungarianCakes: {
        heading: 'Hungarian Cakes in Norwich',
        metaDescription: 'Traditional Hungarian cakes and pastries baked in Norwich: Dobos torte, Esterházy, '
            + 'Somlói galuska, Rigó Jancsi, Gerbeaud, bejgli and more, made to order.',
        intro: 'I grew up in Hungary, where cakes like Dobos and Esterházy are part of every celebration. Now I '
            + 'bake them in Norwich, the way I learned them, for anyone who wants to try them.',
        image: {
            src: '/images/european-cake.jpg', width: 1500, height: 2000, alt: 'A layered Hungarian cake'
        },
        secondaryLink: { to: '/products', label: 'All Products' },
        sections: [
            {
                heading: 'Why Hungarian cakes are different',
                paragraphs: [
                    'Hungarian cakes rely on thin sponge layers, nuts, rich creams and a little caramel or '
                    + 'chocolate, rather than thick icing. Many come from the coffee houses of 19th-century '
                    + 'Budapest and are named after the people who made them famous.'
                ]
            }
        ],
        productsHeading: 'The classics',
        products: [
            {
                slug: 'dobos-cake',
                name: 'Dobos torte',
                blurb: 'Thin sponge layers with chocolate buttercream under a crisp caramel top, created by '
                    + 'confectioner József C. Dobos in the 1880s.'
            },
            {
                slug: 'esterhazy-cake',
                name: 'Esterházy cake',
                blurb: 'Walnut meringue layers and vanilla cream, finished with its famous feathered icing.'
            },
            {
                slug: 'somloi-galuska',
                name: 'Somlói galuska',
                blurb: 'Sponge soaked with rum syrup, layered with vanilla cream, walnuts and raisins, then '
                    + 'topped with chocolate sauce and whipped cream.'
            },
            {
                slug: 'rigo-jancsi-chocolate-slice',
                name: 'Rigó Jancsi',
                blurb: 'A tall chocolate mousse slice named after a famous Hungarian violinist.'
            },
            {
                slug: 'gerbeaud',
                name: 'Gerbeaud slice',
                blurb: 'Layers of pastry, walnuts and apricot jam under dark chocolate, named after Budapest\'s '
                    + 'Café Gerbeaud.'
            },
            {
                slug: 'french-cream-pastry',
                name: 'French cream pastry (franciakrémes)',
                blurb: 'Puff pastry, vanilla custard and whipped cream, with a caramel glaze.'
            },
            {
                slug: 'rakoczi-curd-tart',
                name: 'Rákóczi curd tart',
                blurb: 'Sweet curd cheese on shortcrust, with a lattice of meringue and apricot jam.'
            },
            {
                slug: 'isler',
                name: 'Isler',
                blurb: 'Two shortbread biscuits sandwiched with jam and dipped in chocolate.'
            },
            {
                slug: 'marlenka',
                name: 'Marlenka honey cake',
                blurb: 'Thin honey layers with a soft cream filling, loved across Central Europe.'
            },
            {
                slug: 'hungarian-christmas-roll-bejgli',
                name: 'Bejgli',
                blurb: 'The Hungarian Christmas roll, filled with walnut or poppy seed.'
            },
            {
                slug: 'hungarian-potato-scone',
                name: 'Hungarian potato scone (pogácsa)',
                blurb: 'Savoury, buttery and flaky: the snack served at every Hungarian gathering.'
            },
            {
                slug: 'kaiser-roll',
                name: 'Kaiser roll',
                blurb: 'The crusty bread roll Central Europe eats for breakfast.'
            }
        ]
    },
    delivery: {
        heading: 'Cake Delivery in Norwich',
        metaDescription: 'Cake delivery across Norwich and nearby villages from £5, or collection from north '
            + 'Norwich (NR6) at weekends. Birthday, wedding and celebration cakes.',
        intro: 'I deliver cakes across Norwich and the surrounding villages, or you can collect from my kitchen '
            + 'in north Norwich.',
        image: { src: '/images/categories/cake.jpg', width: 4000, height: 1800, alt: 'A finished celebration cake' },
        secondaryLink: { to: '/products', label: 'Browse Cakes' },
        sections: [
            {
                heading: 'Delivery prices',
                paragraphs: [
                    'Delivery starts from £5 and is priced by distance. Send me your postcode and date, and I '
                    + 'will confirm the exact price with your order.'
                ],
                table: {
                    headers: ['Area', 'Distance from my kitchen'],
                    rows: [
                        ['Old Catton', 'under 1 mile'],
                        ['Horsham St Faith', 'about 1½ miles'],
                        ['Spixworth', 'about 2 miles'],
                        ['Hellesdon', 'about 2 miles'],
                        ['Horsford', 'about 3 miles'],
                        ['Drayton', 'about 3 miles'],
                        ['Norwich city centre', 'about 3 miles'],
                        ['Taverham', 'about 4 miles'],
                        ['Wroxham', 'about 5 miles']
                    ]
                }
            },
            {
                heading: 'Collection',
                paragraphs: [
                    'You can collect from NR6 on Saturdays and Sundays between 10am and 5pm. Please check your '
                    + 'cake when you collect it, as the sale is final once it leaves.'
                ]
            },
            {
                heading: 'Payment',
                paragraphs: [
                    'A deposit secures your order. The balance is due on collection or before delivery, by cash, '
                    + 'card or bank transfer.'
                ]
            }
        ],
        faqs: [
            {
                question: 'Can you deliver on the same day I order?',
                answer: 'I usually need two weeks\' notice, but I sometimes have space for short-notice orders. '
                    + 'Get in touch and I will let you know.'
            },
            {
                question: 'Do you deliver outside Norwich?',
                answer: 'Yes, across much of Norfolk. Delivery further out costs more, so send me your postcode '
                    + 'for a price.'
            }
        ]
    }
} satisfies Record<string, LandingPage>;
